import { useEffect, useState } from 'react';
import { OPPORTUNITY_STATUS_LABELS, type Onboarding } from '@catalyst-psa/shared';

import { ApiError } from '../../api/client.js';
import { Button } from '../../components/ui/Button.js';
import { Modal } from '../../components/ui/Modal.js';
import { TextAreaField, TextField } from '../../components/ui/Field.js';
import { useRegenerateSalesRepLink, useSendSalesRepRequest } from './api.js';

interface SalesRepRequestModalProps {
  isOpen: boolean;
  onboarding: Onboarding;
  onClose: () => void;
}

function salesRepUrlOf(onboarding: Onboarding): string {
  if (onboarding.salesRepToken && onboarding.salesRepEnabled && typeof window !== 'undefined') {
    return `${window.location.origin}/sr/${onboarding.salesRepToken}`;
  }
  return '';
}

/** "Name — link" lines for the customer's OTHER in-progress onboarding records. */
function siblingLines(onboarding: Onboarding): string[] {
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  return onboarding.activeSiblings.map(
    (s) => `${s.opportunityName} — ${origin}/sales/onboarding/${s.id}`,
  );
}

function buildDefaultBody(onboarding: Onboarding, url: string): string {
  const firstName = (onboarding.salesRepName ?? '').split(' ')[0] ?? '';
  const greeting = firstName ? `Hi ${firstName},` : 'Hi,';
  const link = url || '[generate the form link first to insert it here]';
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const opportunityName = onboarding.opportunityName || 'this opportunity';
  const opportunityType = onboarding.opportunityServiceCategoryName;
  const statusLabel = OPPORTUNITY_STATUS_LABELS[onboarding.opportunityStatus];
  const siblings = siblingLines(onboarding);
  const siblingsBlock =
    siblings.length > 0
      ? `\n\nOther active onboarding records for ${onboarding.customerName} (for reference — this request is NOT about these):\n${siblings.join('\n')}`
      : '';
  return `${greeting}

${onboarding.customerName} just reached ${statusLabel} and we're kicking off onboarding for ${opportunityName}.
This is not an automated email. A human pushed a button and is asking for information from you.
Fill out this quick form and your notes will be attached to the onboarding record:

${link}

Form is an opportunity to share any internal context delivery team should know — key stakeholders, billing exceptions, commitments made during the sale, scope nuances, risks, timing constraints, or anything else worth flagging.

WE WILL WAIT FOR THIS INFO BEFORE SPINNING UP THE PROJECT

Opportunity: ${opportunityName}${opportunityType ? ` (${opportunityType})` : ''} — ${origin}/sales/opportunities/${onboarding.opportunityId}
Onboarding record: ${origin}/sales/onboarding/${onboarding.id}

Thanks!
Catalyst Delivery Team${siblingsBlock}`;
}

/**
 * Composes the internal "Sales Rep Info" request email (default TO = the source
 * opportunity's sales rep). The email carries a public /sr/ form link; submitting the
 * form appends the rep's notes to the onboarding record and completes the step.
 */
export function SalesRepRequestModal({
  isOpen,
  onboarding,
  onClose,
}: SalesRepRequestModalProps): JSX.Element {
  const sendMutation = useSendSalesRepRequest();
  const regenMutation = useRegenerateSalesRepLink();
  const resetSend = sendMutation.reset;
  const resetRegen = regenMutation.reset;

  const [to, setTo] = useState('');
  const [cc, setCc] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');

  const formUrl = salesRepUrlOf(onboarding);
  const hasLink = Boolean(formUrl);

  useEffect(() => {
    if (!isOpen) return;
    setTo(onboarding.salesRepEmail ?? '');
    setCc('');
    setSubject(
      `Onboarding info needed — ${onboarding.customerName} — ${onboarding.opportunityName}`,
    );
    setBody(buildDefaultBody(onboarding, salesRepUrlOf(onboarding)));
    resetSend();
    resetRegen();
    // Only reset on open / record change (avoids clobbering edits when the link is generated).
    // eslint config has no exhaustive-deps rule.
  }, [isOpen, onboarding.id, resetSend, resetRegen]);

  const handleGenerateLink = (): void => {
    regenMutation.mutate(onboarding.id, {
      onSuccess: (updated) => {
        const url = updated.salesRepToken
          ? `${window.location.origin}/sr/${updated.salesRepToken}`
          : '';
        setBody(buildDefaultBody(updated, url));
      },
    });
  };

  const handleSend = (): void => {
    sendMutation.mutate(
      {
        id: onboarding.id,
        input: { to: to.trim(), cc: cc.trim() || null, subject: subject.trim(), body },
      },
      { onSuccess: () => onClose() },
    );
  };

  const error =
    sendMutation.error instanceof ApiError
      ? sendMutation.error.message
      : sendMutation.error
        ? 'Failed to send the info request.'
        : null;

  const canSend = Boolean(to.trim() && subject.trim() && body.trim() && hasLink);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Request sales rep info"
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={sendMutation.isPending}>
            Cancel
          </Button>
          <Button onClick={handleSend} isLoading={sendMutation.isPending} disabled={!canSend}>
            Send email
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-slate-500">
          Email the sales rep a form link. Their answers are attached to this onboarding
          record&apos;s notes and the step completes automatically when they submit.
        </p>

        {!onboarding.salesRepEmail ? (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            The source opportunity has no sales rep with an email address — enter a recipient
            manually.
          </div>
        ) : null}

        {hasLink ? (
          <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs">
            <span className="text-slate-500">Form link included: </span>
            <span className="break-all font-medium text-slate-800">{formUrl}</span>
          </div>
        ) : (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-3 text-sm text-amber-800">
            <p>No active form link yet — generate one to include in the email.</p>
            <Button
              variant="secondary"
              size="sm"
              className="mt-2"
              isLoading={regenMutation.isPending}
              onClick={handleGenerateLink}
            >
              Generate form link
            </Button>
          </div>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <TextField label="To" value={to} onChange={(e) => setTo(e.target.value)} />
          <TextField label="CC" value={cc} onChange={(e) => setCc(e.target.value)} />
        </div>

        <TextField
          label="Subject"
          value={subject}
          maxLength={300}
          onChange={(e) => setSubject(e.target.value)}
        />

        <TextAreaField
          label="Message"
          value={body}
          rows={10}
          onChange={(e) => setBody(e.target.value)}
        />

        {error ? <p className="text-sm text-rose-600">{error}</p> : null}
      </div>
    </Modal>
  );
}
